import { momentDateTime, momentTime } from '../dates';
import { useState } from 'react';
import { activeOrganizationId, graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

type NetworkRequest = { at: string; initiator: 'TOOL' | 'PAGE'; method: string; url: string; host: string | null; status: number | null;
  responseBytes: number | null; outcome: 'RESPONDED' | 'REFUSED' | 'FAILED'; reason: string | null; requestBody: string | null; requestBodyTruncated: boolean };
type RunEvent = { sequence: string; kind: string; at: string; data: Record<string, unknown> };
type RunLog = { id: string; strategy: string; status: string; attempts: Array<{ id: string; number: number; adapter: string; status: string;
  startedAt: string; completedAt: string | null; failureCode: string | null; failureDetail?: string | null; events: RunEvent[] }> };

const time = (value: string) => momentTime(value);
const compact = (value: unknown) => { const text = JSON.stringify(value); return text.length > 300 ? `${text.slice(0, 300)}…` : text; };

function Requests({ requests }: { requests: NetworkRequest[] }) {
  return <div className="data-table" tabIndex={0}><table><thead><tr><th>Time</th><th>Asked by</th><th>Request</th><th>Result</th><th>Sent</th></tr></thead>
    <tbody>{requests.map((request, index) => <tr key={index}>
      <td>{time(request.at)}</td><td>{request.initiator === 'TOOL' ? 'Researcher' : 'Page'}</td>
      <td>{request.method} <span style={{ wordBreak: 'break-all' }}>{request.url}</span></td>
      <td>{request.outcome === 'RESPONDED' ? `${request.status} · ${request.responseBytes ?? 0} bytes` : `${request.outcome === 'REFUSED' ? 'Refused' : 'Failed'}: ${request.reason ?? ''}`}</td>
      <td>{request.requestBody ? <code style={{ wordBreak: 'break-all' }}>{request.requestBody}{request.requestBodyTruncated ? '…' : ''}</code> : ''}</td>
    </tr>)}</tbody></table></div>;
}

function Event({ event }: { event: RunEvent }) {
  const data = event.data;
  if (event.kind === 'TOOL_REQUESTED') return <li><strong>{time(event.at)} · Step {String(data.number)}: {String(data.name)}</strong> <code>{compact(data.arguments)}</code></li>;
  if (event.kind === 'TOOL_FAILED') return <li>{time(event.at)} · Step {String(data.number)} failed: {String(data.reason)}</li>;
  if (event.kind === 'TOOL_OBSERVED') {
    const sources = (data.sources as Array<{ url: string; file: string }> | undefined) ?? [];
    return <li>{time(event.at)} · Step {String(data.number)} returned{sources.length ? `, capturing ${sources.map(source => `${source.file} from ${source.url}`).join(', ')}` : ''}</li>;
  }
  if (event.kind === 'NETWORK_REQUESTS') {
    const requests = (data.requests as NetworkRequest[] | undefined) ?? [];
    return <li><details><summary>{time(event.at)} · {requests.length} web {requests.length === 1 ? 'request' : 'requests'} during step {String(data.number)}
      {requests.some(request => request.outcome === 'REFUSED') ? ' (some refused)' : ''}{data.dropped ? ` · ${String(data.dropped)} not kept` : ''}</summary>
      <Requests requests={requests} /></details></li>;
  }
  return <li>{time(event.at)} · {event.kind.toLowerCase().replaceAll('_', ' ')} <code>{compact(data)}</code></li>;
}

/**
 * Staff view of one research job's audit log: each attempt, the steps the
 * researcher took, every web request those steps and the pages they opened
 * made, and how the attempt ended. The full log, including captured page
 * content, downloads as JSON.
 */
export function ResearchRunLog({ jobId }: { jobId: string }) {
  const { token } = useAuth();
  const [log, setLog] = useState<RunLog | null>(null);
  const [error, setError] = useState('');
  const load = (full: boolean) => graphql<{ researchRunLog: RunLog }>({ query: 'query RunLog($id:ID!,$full:Boolean){researchRunLog(jobId:$id,full:$full)}',
    variables: { id: jobId, full }, token: token!, organizationId: activeOrganizationId() }).then(data => data.researchRunLog);
  async function open() {
    if (log || !token) return;
    try { setLog(await load(false)); } catch (reason) { setError((reason as Error).message); }
  }
  async function download() {
    if (!token) return;
    try {
      const full = await load(true);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([JSON.stringify(full, null, 2)], { type: 'application/json' }));
      link.download = `research-run-${jobId}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (reason) { setError((reason as Error).message); }
  }
  return <details onToggle={event => { if ((event.target as HTMLDetailsElement).open) void open(); }}>
    <summary>Full run log</summary>
    {error && <div className="alert error">{error}</div>}
    {!log && !error && <p>Loading…</p>}
    {log && <>
      <button type="button" className="button button-quiet" onClick={() => void download()}>Download the full log</button>
      {log.attempts.length === 0 && <p>This research has not run yet.</p>}
      {log.attempts.map(attempt => <article key={attempt.id} aria-label={`Attempt ${attempt.number}`}>
        <h4>Attempt {attempt.number} · {attempt.adapter} · {attempt.status.toLowerCase()}{attempt.failureCode ? ` (${attempt.failureCode})` : ''}</h4>
        <p>Started {momentDateTime(attempt.startedAt)}{attempt.completedAt ? `, finished ${momentDateTime(attempt.completedAt)}` : ''}</p>
        {attempt.failureCode === 'INTERRUPTED' && <p>Stopped because the research worker restarted; it does not count as a failure and research carried on.</p>}
        {attempt.failureDetail && <p className="alert">Why it failed: {attempt.failureDetail}</p>}
        <ol>{attempt.events.map(event => <Event key={event.sequence} event={event} />)}</ol>
      </article>)}
    </>}
  </details>;
}
